import {ActionTree, GetterTree, Module, MutationTree} from 'vuex'
import {SentinelState, RootState} from '@/types/store'
import SentinelResponse from '@/model/response/SentinelResponse'
import {AppConfig} from '@/config/app.config'
import {difference, toPairs} from 'lodash'
import * as Helper from '@/helpers'

// initial state
const state: SentinelState = {
  // the last response from server
  currentResponse: null,

  // the second from the last response from server
  previousResponse: null,

  // the difference from between the current and the previous response in order to get new values
  result: new SentinelResponse(),

  intervalId: null,
  intervalTime: AppConfig.sentinelWatchInterval,
  eventListener: {
    start: [],
    stop: [],
    shutdown: [],
    update: [],
  },
}

// getters
const getters: GetterTree<SentinelState, RootState> = {
  currentResponse: ({currentResponse}) => currentResponse,
  previousResponse: ({previousResponse}) => previousResponse,
  result: ({result}) => result,
  intervalTime: ({intervalTime}) => intervalTime >= 3000 ? intervalTime : 3000,
}

// actions
const actions: ActionTree<SentinelState, RootState> = {

  /**
   * Start sentinel process
   * @param commit
   * @param state
   * @param getters
   * @param dispatch
   */
  start: async ({commit, state, getters, dispatch}) => {
    await dispatch('requestSentinel')
    const handler = () => dispatch('requestSentinel')
    commit('SET_INTERVAL_ID', window.setInterval(handler, getters.intervalTime))
    state.eventListener.start.forEach((item) => item(state.intervalId))
  },

  /**
   * Stop sentinel process
   * @param commit
   * @param state
   */
  stop: ({commit, state}) => {
    if (state.intervalId) {
      window.clearInterval(state.intervalId)
    }
    commit('SET_INTERVAL_ID', null)
    state.eventListener.stop.forEach((item) => item())
  },

  /**
   * Stop sentinel process
   * @param state
   */
  reset: ({commit}) => commit('RESET_RESPONSE'),

  /**
   * Stop and reset the sentinel process
   * @param state
   * @param dispatch
   */
  shutdown: ({state, dispatch}) => {
    dispatch('stop')
    dispatch('reset')
    state.eventListener.shutdown.forEach((item) => item())
  },

  /**
   * Request sentinel data
   * @param state
   */
  requestSentinel: async ({commit}) => {
    try {
      const sentinelResponse = new SentinelResponse()
      await sentinelResponse.getSentinelData()
      commit('SET_RESPONSE', sentinelResponse)
      state.eventListener.update.forEach((item) => item(sentinelResponse))
    } catch (e) {
      /* ignore error */
      state.eventListener.update.forEach((item) => item(null))
    }
  },

  /**
   * Set interval time
   * @param commit
   * @param dispatch
   * @param val
   */
  setIntervalTime: async ({commit, dispatch}, val) => {
    commit('SET_INTERVAL_TIME', val)
    await dispatch('stop')
    await dispatch('start')
  },

  /**
   * On Start Event
   * @param dispatch
   * @param callback
   */
  onStart: ({dispatch}, callback) => {
    dispatch('removeEventListener', {name: 'start'})
    dispatch('addEventListener', {name: 'start', callback})
  },

  /**
   * On Stop Event
   * @param dispatch
   * @param callback
   */
  onStop: ({dispatch}, callback) => {
    dispatch('removeEventListener', {name: 'stop'})
    dispatch('addEventListener', {name: 'stop', callback})
  },

  /**
   * On Shutdown Event
   * @param dispatch
   * @param callback
   */
  onShutdown: ({dispatch}, callback) => {
    dispatch('removeEventListener', {name: 'shutdown'})
    dispatch('addEventListener', {name: 'shutdown', callback})
  },

  /**
   * On Update Event
   * @param dispatch
   * @param callback
   */
  onUpdate: ({dispatch}, callback) => {
    dispatch('removeEventListener', {name: 'update'})
    dispatch('addEventListener', {name: 'update', callback})
  },

  /**
   * Add event listener
   * @param commit
   * @param payload {name, callback}
   */
  addEventListener: ({commit}, payload) => commit('ADD_EVENT_LISTENER', payload),

  /**
   * Remove event listener
   * @param commit
   * @param payload
   */
  removeEventListener: ({commit}, payload) => commit('REMOVE_EVENT_LISTENER', payload),
}

// mutations
const mutations: MutationTree<SentinelState> = {
  // Set Response mutation
  SET_RESPONSE(state, response: SentinelResponse) {
    if (state.currentResponse) {
      state.previousResponse = state.currentResponse.clone()
    }

    state.currentResponse = response

    if (state.previousResponse) {
      const currentResponse: any = Helper.classToPlain(state.currentResponse)
      const previousResponse: any = Helper.classToPlain(state.previousResponse)

      const result: any = {}
      toPairs(currentResponse).forEach((pair) => {
        result[pair[0]] = difference(currentResponse[pair[0]], previousResponse[pair[0]])
      })

      Helper.plainToClassFromExist(state.result, result)
    }
  },

  // Reset Response mutation
  RESET_RESPONSE(state) {
    state.previousResponse = null
    state.currentResponse = null
    state.result = new SentinelResponse()
  },

  // Set Interval Id mutation
  SET_INTERVAL_ID(state, val) {
    state.intervalId = val
  },

  // Set Interval Time mutation
  SET_INTERVAL_TIME(state, val) {
    state.intervalTime = val
  },

  // Add Event Listener mutation
  ADD_EVENT_LISTENER(state, {name, callback}) {
    state.eventListener[name].push(callback)
  },

  // Remove Event Listener mutation
  REMOVE_EVENT_LISTENER(state, {name, callback}) {
    if (callback) {
      const index = state.eventListener[name].findIndex((item) => item === callback)
      state.eventListener[name].splice(index, 1)
    } else {
      state.eventListener[name] = []
    }
  },

}

const namespaced: boolean = true
export const sentinel: Module<SentinelState, RootState> = {
  namespaced,
  state,
  getters,
  actions,
  mutations,
}
